import {captureException} from '@sentry/browser';
import {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  invokeVoiceTool,
  persistVoiceTranscript,
  PersistVoiceTranscriptRequest,
  startWebRTCRealtimeVoiceSession,
} from 'llm/data-capture/DataCaptureDAO';
import {
  isAssistantOutputItemAddedEvent,
  isAssistantTranscriptDoneEvent,
  isConversationItemCreatedEvent,
  isFunctionCallDoneEvent,
  isInputAudioBufferClearedEvent,
  isInputAudioBufferSpeechStartedEvent,
  isInputAudioBufferSpeechStoppedEvent,
  isOutputAudioBufferClearedEvent,
  isOutputAudioBufferStartedEvent,
  isOutputAudioBufferStoppedEvent,
  isResponseDoneEvent,
  isUserTranscriptionCompletedEvent,
  isUserTranscriptionFailedEvent,
  createToolResponseEvent,
  createTriggerInferenceEvent,
} from 'llm/realtime-voice/OpenAIRealtimeEvents';
import {
  clearPendingVoiceTranscripts,
  markPendingVoiceTranscriptPersisted,
  removePendingVoiceTranscript,
  upsertPendingVoiceTranscript,
} from 'llm/realtime-voice/PendingVoiceTranscripts';

import WebRTCSession from './WebRTCSession';

// Persist a terminal fallback rather than silently losing a stalled user transcription.
const USER_TRANSCRIPTION_TIMEOUT_MS = 15_000;
const WEBRTC_DISCONNECT_GRACE_MS = 5_000;

export enum RealtimeVoiceSessionState {
  NOT_CONNECTED = 'NOT_CONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export interface RealtimeVoiceSessionControls {
  sessionState: RealtimeVoiceSessionState;
  isUserSpeaking: boolean;
  isAssistantSpeaking: boolean;
  microphoneLevel: number;
  startVoiceSession: () => Promise<void>;
  stopVoiceSession: () => Promise<void>;
}

interface VoiceSessionAttempt {
  session: WebRTCSession | null;
}

/**
 * Hook to manage a realtime voice session.
 *
 * Currently this is implemented as a WebRTC connection to OpenAI's Realtime API.
 */
export default function useRealtimeVoiceSession(chatSessionId: string | null): RealtimeVoiceSessionControls {
  const [sessionState, setSessionState] = useState<RealtimeVoiceSessionState>(
    RealtimeVoiceSessionState.NOT_CONNECTED
  );
  const webRTCSessionRef = useRef<WebRTCSession | null>(null);
  const activeSessionAttemptRef = useRef<VoiceSessionAttempt | null>(null);
  const sessionStartTimeMsRef = useRef<number | null>(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const speakingUserItemIdsRef = useRef<Set<string>>(new Set());
  const playingAssistantResponseIdsRef = useRef<Set<string>>(new Set());
  // Tracks whether the component is still mounted. Used to detect unmount during async operations
  // where webRTCSessionRef.current may not yet be set (e.g., during WebRTCSession.create()).
  const isMountedRef = useRef(true);
  // Capture ordering before asynchronous user transcription completes.
  const previousItemIdByItemIdRef = useRef<Map<string, string | null>>(new Map());
  // Pending never-drop timeouts for user items awaiting transcription, keyed by item id.
  const pendingTranscriptionTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // User placeholders created by speech_started but not yet committed as conversation items.
  const earlyUserItemIdsRef = useRef<Set<string>>(new Set());
  // Assistant output items are announced before their transcript and are correlated to response
  // failures by response ID.
  const assistantItemIdByResponseIdRef = useRef<Map<string, string>>(new Map());
  const disconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingTranscriptionTimeout = useCallback((itemId: string) => {
    const timeoutId = pendingTranscriptionTimeoutsRef.current.get(itemId);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      pendingTranscriptionTimeoutsRef.current.delete(itemId);
    }
  }, []);

  const clearPendingTranscriptState = useCallback(() => {
    pendingTranscriptionTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pendingTranscriptionTimeoutsRef.current.clear();
    earlyUserItemIdsRef.current.clear();
    assistantItemIdByResponseIdRef.current.clear();
    if (chatSessionId != null) {
      clearPendingVoiceTranscripts(chatSessionId);
    }
  }, [chatSessionId]);

  const clearDisconnectTimeout = useCallback(() => {
    if (disconnectTimeoutRef.current != null) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  }, []);

  // Disconnect, release the microphone, and cancel pending transcript fallbacks on unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      activeSessionAttemptRef.current = null;
      const webRTCSession = webRTCSessionRef.current;
      webRTCSessionRef.current = null;
      webRTCSession?.close().catch(captureException);
      clearDisconnectTimeout();
      clearPendingTranscriptState();
    };
  }, [clearDisconnectTimeout, clearPendingTranscriptState]);

  const persistTranscript = useCallback(
    async (role: 'user' | 'assistant', text: string, itemId: string, failed = false) => {
      assert(chatSessionId != null, 'Chat session ID should be set during voice session');
      const request: PersistVoiceTranscriptRequest = {role, text, itemId};
      const previousItemId = previousItemIdByItemIdRef.current.get(itemId);
      if (previousItemId !== undefined) {
        request.previousItemId = previousItemId;
      }
      if (failed) {
        request.failed = true;
      }
      const {eventId} = await persistVoiceTranscript(chatSessionId, request);
      markPendingVoiceTranscriptPersisted(chatSessionId, itemId, eventId);
    },
    [chatSessionId]
  );

  const handleDataEvent = useCallback(
    async (data: unknown) => {
      assert(chatSessionId != null, 'Chat session ID should be set during voice session');
      // Capture the current session at the start. After awaits, we verify we're still on the same
      // session to prevent sending responses to a different session if the user restarts voice.
      const webRTCSession = webRTCSessionRef.current;
      if (webRTCSession == null) {
        return;
      }
      if (isFunctionCallDoneEvent(data)) {
        // Handle OpenAI tool calls. For now, we block on tool call completion and send the result
        // to the model, and we expect errors for the LLM to be sent as regular text results.
        // In the future, we may make this more nuanced to allow truly async tool calls that don't
        // stop the conversation while they run.
        const {name, call_id: callId, arguments: args} = data.item;
        const {output} = await invokeVoiceTool(chatSessionId, {name, callId, args});
        // Verify we're still on the same session - abort if unmounted or session was restarted.
        if (webRTCSessionRef.current !== webRTCSession) {
          return;
        }
        webRTCSession.sendDataEvent(createToolResponseEvent(callId, output));
        webRTCSession.sendDataEvent(createTriggerInferenceEvent());
        return;
      }

      if (isInputAudioBufferSpeechStartedEvent(data)) {
        speakingUserItemIdsRef.current.add(data.item_id);
        setIsUserSpeaking(true);
        earlyUserItemIdsRef.current.add(data.item_id);
        upsertPendingVoiceTranscript(chatSessionId, {
          itemId: data.item_id,
          previousItemId: null,
          role: 'user',
        });
        return;
      }
      if (isInputAudioBufferSpeechStoppedEvent(data)) {
        speakingUserItemIdsRef.current.delete(data.item_id);
        setIsUserSpeaking(speakingUserItemIdsRef.current.size > 0);
        return;
      }
      if (isInputAudioBufferClearedEvent(data)) {
        speakingUserItemIdsRef.current.clear();
        setIsUserSpeaking(false);
        earlyUserItemIdsRef.current.forEach((itemId) => {
          removePendingVoiceTranscript(chatSessionId, itemId);
        });
        earlyUserItemIdsRef.current.clear();
        return;
      }
      if (isOutputAudioBufferStartedEvent(data)) {
        playingAssistantResponseIdsRef.current.add(data.response_id);
        setIsAssistantSpeaking(true);
        return;
      }
      if (isOutputAudioBufferStoppedEvent(data) || isOutputAudioBufferClearedEvent(data)) {
        playingAssistantResponseIdsRef.current.delete(data.response_id);
        setIsAssistantSpeaking(playingAssistantResponseIdsRef.current.size > 0);
        return;
      }
      if (isAssistantOutputItemAddedEvent(data)) {
        assistantItemIdByResponseIdRef.current.set(data.response_id, data.item.id);
        upsertPendingVoiceTranscript(chatSessionId, {
          itemId: data.item.id,
          previousItemId: null,
          role: 'assistant',
        });
        return;
      }
      if (isResponseDoneEvent(data)) {
        const assistantItemId = assistantItemIdByResponseIdRef.current.get(data.response.id);
        assistantItemIdByResponseIdRef.current.delete(data.response.id);
        if (assistantItemId != null && data.response.status !== 'completed') {
          removePendingVoiceTranscript(chatSessionId, assistantItemId);
        }
        return;
      }
      if (isConversationItemCreatedEvent(data)) {
        const itemId = data.item.id;
        previousItemIdByItemIdRef.current.set(itemId, data.previous_item_id);
        if (data.item.role === 'user' || data.item.role === 'assistant') {
          upsertPendingVoiceTranscript(chatSessionId, {
            itemId,
            previousItemId: data.previous_item_id,
            role: data.item.role,
          });
        }
        // Only user items go through asynchronous STT and can fail or time out.
        if (data.item.role === 'user') {
          earlyUserItemIdsRef.current.delete(itemId);
          clearPendingTranscriptionTimeout(itemId);
          const timeoutId = setTimeout(() => {
            pendingTranscriptionTimeoutsRef.current.delete(itemId);
            persistTranscript('user', '', itemId, true).catch((error) => {
              captureException(error, {tags: {'voice-transcript-persist-phase': 'timeout'}});
            });
          }, USER_TRANSCRIPTION_TIMEOUT_MS);
          pendingTranscriptionTimeoutsRef.current.set(itemId, timeoutId);
        }
        return;
      }
      if (isAssistantTranscriptDoneEvent(data)) {
        try {
          await persistTranscript('assistant', data.transcript, data.item_id);
        } catch (error) {
          removePendingVoiceTranscript(chatSessionId, data.item_id);
          throw error;
        }
        return;
      }
      if (isUserTranscriptionCompletedEvent(data)) {
        await persistTranscript('user', data.transcript, data.item_id);
        clearPendingTranscriptionTimeout(data.item_id);
        return;
      }
      if (isUserTranscriptionFailedEvent(data)) {
        await persistTranscript('user', '', data.item_id, true);
        clearPendingTranscriptionTimeout(data.item_id);
      }
    },
    [chatSessionId, clearPendingTranscriptionTimeout, persistTranscript]
  );

  const stopVoiceSession = useCallback(async () => {
    const sessionStartTimeMs = sessionStartTimeMsRef.current;
    sessionStartTimeMsRef.current = null;
    activeSessionAttemptRef.current = null;
    const webRTCSession = webRTCSessionRef.current;
    webRTCSessionRef.current = null;

    const closePromise =
      webRTCSession == null
        ? null
        : Promise.resolve(webRTCSession.close()).catch((error) => {
            captureException(error);
          });
    clearDisconnectTimeout();
    clearPendingTranscriptState();
    speakingUserItemIdsRef.current.clear();
    playingAssistantResponseIdsRef.current.clear();
    setIsUserSpeaking(false);
    setIsAssistantSpeaking(false);
    setMicrophoneLevel(0);
    setSessionState(RealtimeVoiceSessionState.NOT_CONNECTED);
    if (closePromise != null) {
      await closePromise;
    }

    // Track analytics for session end. Note that we don't currently track this when closing the
    // tab or navigating away from the page.
    if (chatSessionId != null && sessionStartTimeMs != null) {
      const durationMs = Date.now() - sessionStartTimeMs;
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ENDED_COMPOSE_REALTIME_VOICE_SESSION, {
        chatSessionId,
        durationMs,
      });
    }
  }, [chatSessionId, clearDisconnectTimeout, clearPendingTranscriptState]);

  const handleWebRTCConnectionStateChange = useCallback(
    (connectionState: RTCPeerConnectionState) => {
      if (connectionState === 'connected') {
        clearDisconnectTimeout();
        return;
      }
      if (connectionState === 'disconnected') {
        if (disconnectTimeoutRef.current == null) {
          disconnectTimeoutRef.current = setTimeout(() => {
            disconnectTimeoutRef.current = null;
            stopVoiceSession().catch(captureException);
          }, WEBRTC_DISCONNECT_GRACE_MS);
        }
        return;
      }
      if (
        (connectionState === 'failed' || connectionState === 'closed') &&
        webRTCSessionRef.current != null
      ) {
        stopVoiceSession().catch(captureException);
      }
    },
    [clearDisconnectTimeout, stopVoiceSession]
  );

  const startVoiceSession = useCallback(async () => {
    assert(chatSessionId != null, 'Unexpectedly started voice session without a chat session ID.');
    if (activeSessionAttemptRef.current != null) {
      return;
    }
    const attempt: VoiceSessionAttempt = {session: null};
    activeSessionAttemptRef.current = attempt;
    setSessionState(RealtimeVoiceSessionState.CONNECTING);

    try {
      // Create into a local variable first, then check if we should abort before assigning.
      // This handles unmount during WebRTCSession.create() (e.g., during mic permission prompt).
      const webRTCSession = await WebRTCSession.create(
        (data) => {
          if (activeSessionAttemptRef.current !== attempt || attempt.session == null) {
            return;
          }
          return handleDataEvent(data);
        },
        (connectionState) => {
          if (activeSessionAttemptRef.current === attempt && attempt.session != null) {
            handleWebRTCConnectionStateChange(connectionState);
          }
        },
        (level) => {
          if (isMountedRef.current && activeSessionAttemptRef.current === attempt) {
            setMicrophoneLevel(level);
          }
        }
      );
      if (!isMountedRef.current || activeSessionAttemptRef.current !== attempt) {
        await webRTCSession.close();
        return;
      }
      attempt.session = webRTCSession;
      webRTCSessionRef.current = webRTCSession;

      const initialSDP = await webRTCSession.createSDPOffer();
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      const {sdpAnswer} = await startWebRTCRealtimeVoiceSession(chatSessionId, initialSDP);
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      await webRTCSession.startSessionWithSDPAnswer(sdpAnswer);
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      setSessionState(RealtimeVoiceSessionState.CONNECTED);
      sessionStartTimeMsRef.current = Date.now();
    } catch (error) {
      if (activeSessionAttemptRef.current !== attempt) {
        return;
      }
      await stopVoiceSession();
      throw error;
    }
  }, [chatSessionId, stopVoiceSession, handleDataEvent, handleWebRTCConnectionStateChange]);

  return {
    sessionState,
    isUserSpeaking,
    isAssistantSpeaking,
    microphoneLevel,
    startVoiceSession,
    stopVoiceSession,
  };
}
